import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import './colleagues-carousel.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (name = '') => {
  const parts = name.replace(/^(Dr\.|Mr\.|Mrs\.|Ms\.|Prof\.)\s*/i, '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * "What Colleagues Say About Krina" — full-width auto-scrolling marquee.
 * Same UX pattern as ReviewsCarousel but for peer endorsements.
 *
 * FEATURE FLAG: rendered ONLY when `REACT_APP_COLLEAGUES_ENABLED === 'true'`.
 * This is set to `true` in preview and intentionally omitted from production
 * env vars — the section stays hidden on production redeploys until the
 * admin explicitly adds the flag in the Emergent deployment dashboard.
 *
 * Even when the flag is enabled, we silently render nothing when the public
 * `/api/colleagues` list is empty (i.e. no colleagues published yet). Two
 * layers of protection so no half-built section ever appears on the homepage.
 */
const COLLEAGUES_ENABLED = process.env.REACT_APP_COLLEAGUES_ENABLED === 'true';

const ColleaguesCarouselInner = () => {
  const [colleagues, setColleagues] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const idleTimerRef = useRef(null);
  const trackRef = useRef(null);

  const SECONDS_PER_CARD = 6;
  const RESUME_AFTER_IDLE_MS = 4000;

  const pauseAndResume = useCallback(() => {
    setIsPaused(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIsPaused(false), RESUME_AFTER_IDLE_MS);
  }, []);

  useEffect(() => () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/colleagues`);
        if (!cancelled) setColleagues(res.data || []);
      } catch {
        if (!cancelled) setColleagues([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const step = useCallback((direction) => {
    pauseAndResume();
    const node = trackRef.current;
    if (!node) return;
    const firstCard = node.querySelector('.colleagues-marquee-card');
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width
      + parseFloat(getComputedStyle(node).gap || '0');
    node.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  }, [pauseAndResume]);

  // Hide the entire section until we have at least one published endorsement.
  if (!loaded || colleagues.length === 0) return null;

  const totalDurationSec = colleagues.length * SECONDS_PER_CARD;

  return (
    <section
      className="colleagues-marquee-section"
      aria-label="What colleagues say about Krina"
      data-testid="colleagues-carousel"
    >
      <div className="colleagues-marquee-headline">
        <span className="colleagues-marquee-eyebrow">
          <Quote size={13} /> Peer endorsements
        </span>
        <h2 className="colleagues-marquee-title">What colleagues say about Krina</h2>
        <p className="colleagues-marquee-sub">
          Endorsements from the clinicians who refer their patients to The TMD Physio.
        </p>
      </div>

      <div
        className="colleagues-marquee-viewport"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
        onTouchStart={pauseAndResume}
        onScroll={pauseAndResume}
      >
        <button
          type="button"
          className="colleagues-marquee-nav colleagues-marquee-nav-prev"
          onClick={() => step(-1)}
          aria-label="Previous endorsement"
          data-testid="colleagues-marquee-prev"
        >
          <ChevronLeft size={18} />
        </button>

        <div
          className={`colleagues-marquee-track ${isPaused ? 'is-paused' : ''}`}
          ref={trackRef}
          data-testid="colleagues-marquee-track"
          style={{ '--marquee-duration': `${totalDurationSec * 2}s` }}
        >
          {[...colleagues, ...colleagues].map((c, idx) => {
            const hasClinicLink = c.clinic_url && /^https?:\/\//.test(c.clinic_url);
            return (
              <article
                key={`marquee-${c.id}-${idx}`}
                className="colleagues-marquee-card"
                aria-hidden={idx >= colleagues.length ? 'true' : undefined}
              >
                <Quote size={22} className="colleagues-marquee-quote-icon" aria-hidden="true" />
                {c.quote && <p className="colleagues-marquee-text">&ldquo;{c.quote}&rdquo;</p>}
                <footer className="colleagues-marquee-footer">
                  {c.photo_url
                    ? <img src={c.photo_url} alt="" className="colleagues-marquee-photo" loading="lazy" />
                    : <span className="colleagues-marquee-avatar" aria-hidden="true">{initials(c.name)}</span>}
                  <span className="colleagues-marquee-meta">
                    <strong>{c.name}</strong>
                    {c.credentials && <small className="colleagues-marquee-creds">{c.credentials}</small>}
                    {c.role && <small>{c.role}</small>}
                    {c.clinic_name && (
                      hasClinicLink
                        ? (
                          <a
                            href={c.clinic_url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="colleagues-marquee-clinic"
                            data-testid={`colleagues-clinic-link-${c.id}`}
                          >
                            {c.clinic_name} <ExternalLink size={10} aria-hidden="true" />
                          </a>
                        )
                        : <small className="colleagues-marquee-clinic-plain">{c.clinic_name}</small>
                    )}
                  </span>
                </footer>
              </article>
            );
          })}
        </div>

        <button
          type="button"
          className="colleagues-marquee-nav colleagues-marquee-nav-next"
          onClick={() => step(1)}
          aria-label="Next endorsement"
          data-testid="colleagues-marquee-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </section>
  );
};

// Public wrapper that gates the entire component behind the feature flag.
// Kept outside the inner component so the flag check does not sit above the
// useState/useEffect hooks (React's Rules of Hooks).
const ColleaguesCarousel = () => (COLLEAGUES_ENABLED ? <ColleaguesCarouselInner /> : null);

export default ColleaguesCarousel;